/**
 * Sol Range Slider Wrapper Component
 *
 * A wrapper component for the Sol Range Slider that provides a simplified interface
 * for integrating range slider functionality into other components.
 *
 * @example
 * Basic usage:
 * ```html
 * <cv-sol-range-slider-wrapper
 *   [min]="0"
 *   [max]="100"
 *   [metricConversionType]="0"
 *   (rangeChange)="onRangeChange($event)"
 * </cv-sol-range-slider-wrapper>
 * ```
 *
 * @example
 * Advanced usage with custom configuration:
 * ```html
 * <cv-sol-range-slider-wrapper
 *   [min]="0"
 *   [max]="36000"
 *   [metricConversionType]="1"
 *   [metricDirection]="1"
 *   [defaultRanges]="3"
 *   [connect]="[true, true, false, true, true]"
 *   [colorClasses]="['red', 'blue', 'green']"
 *   [startPoints]="[10, 20, 30]"
 *   (rangeChange)="onRangeChange($event)"
 *   (rangesError)="onRangesError($event)">
 * </cv-sol-range-slider-wrapper>
 * ```
 *
 * @example
 * Component integration:
 * ```typescript
 * export class MyComponent {
 *   minValue = 0;
 *   maxValue = 100;
 *  
 *   onRangeChange(event: { min: number; max: number }) {
 *     this.minValue = event.min;
 *     this.maxValue = event.max;
 *   }
 * }
 * ```
 *
 * REQUIRED INPUT PROPERTIES:
 * - None (all inputs have default values)
 *
 * OPTIONAL INPUT PROPERTIES:
 * @param {number} min - Minimum value for the slider (default: 0)
 * @param {number} max - Maximum value for the slider (default: 36000)
 * @param {number} metricConversionType - Conversion type: 0=percentage, 1=seconds, 2=minutes, 3=hours, 4=days, 5=number (default: 0)
 * @param {number} metricDirection - Direction: 1=increasing, 0=decreasing (default: 0)
 * @param {boolean[]} connect - Connection configuration for ranges (default: [true, true, false, true, true])
 * @param {number} defaultRanges - Default number of ranges (default: 5)
 * @param {string[]} colorClasses - CSS classes for range colors (default: [])
 * @param {(string|number)[]} startPoints - Initial start points for ranges (default: [])
 *
 * OUTPUT EVENTS:
 * @param {EventEmitter} rangeChange - Emits when min/max values change: { min: number, max: number }
 *
 * PUBLIC METHODS:
 * - removeRangeFromSlider(): Removes a range from the slider
 * - addRangeToSlider(): Adds a new range to the slider
 */

import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild } from "@angular/core";
import { isEqual } from "lodash";
import { ThresholdErrorTypes } from "src/app/dashboard/modals/widget-thresholds-modal/widget-thresholds-modal.model";
import { CxoneRangeSliderComponent } from "../cxone-range-slider/cxone-range-slider.component";
import { CVLocalizationService } from "../services/cv-localization.service";
import { MetricConversionType, RANGE_SLIDER_ERRORS, RangeModel } from "./models/range-slider-model";

@Component({
    selector: "cv-sol-range-slider-wrapper",
    templateUrl: "./sol-range-slider-wrapper.component.html",
    styleUrl: "./sol-range-slider-wrapper.component.scss",
})
export class SolRangeSliderWrapperComponent implements OnChanges {
    @Input() min: number = 0; // Default to 0
    @Input() max: number = 100; // Default to 100 percentage
    @Input() metricConversionType: number = 0; // 5 for percentage, 1 for seconds, 2 for minutes, 3 for hours, 4 for days, 0 for number
    @Input() metricDirection: number = 0; // 1 for increasing, 0 for decreasing
    @Input() connect: boolean[] = [true, true, false, true, true];
    @Input() defaultRanges: number = 5;
    @Input() colorClasses: string[] = [];
    @Input() startPoints: (string | number)[] = [];
    @Input() resetThresholdErrors: boolean = false; // New input to reset thresholds ranges in case of cancel.
    @Input() isQualityEvaluationWidget = false;
    // New input for patching threshold data in edit mode
    @Input() thresholdData: RangeModel[] = [];

    @Output() rangesCreated = new EventEmitter<RangeModel[]>();
    @Output() rangesError = new EventEmitter<ThresholdErrorTypes>();
    @Output() rangeChange = new EventEmitter<{ min: number; max: number }>();

    @ViewChild("cxoneRangeSlider") cxoneRangeSlider!: CxoneRangeSliderComponent;
    @ViewChild('minValue', { static: false }) minValueInput;
    @ViewChild('maxValue', { static: false }) maxValueInput;

    private prevMinValue: number = this.min;
    private prevMaxValue: number = this.max;

    get isTimeMetric(): boolean {
        return this.metricConversionType === 4;
    }

    public minError: string;
    public maxError: string;
    public isConflictOverrideError: boolean;
    public minMaxError = false;
    public minValueUpdated = false;
    public maxValueUpdated = false;

    public defaultMarkerValues: number = 11; // Default marker values for the slider
    public showSlider: boolean = true;
    public range: any;
    public pips: any;
    readonly maxSafeInteger = Number.MAX_SAFE_INTEGER;
    public localization: Map<string, string>;
    public isExistingData = false;
    private readonly percentValueToAdd = 10;
    public minValueLabelText = "MINIMUM VALUE";
    public maxValueLabelText = "MAXIMUM VALUE";

    constructor(
        private readonly localizationService: CVLocalizationService,
    ) { }

    ngOnInit() {
        this.initializeComponentAsync();
    }

    async initializeComponentAsync() {
        this.localization = await this.localizationService.GetSection("CXOne_Range_Slider");
        this.minValueLabelText = this.getLocalizationString("Min_Value_Title", "MINIMUM VALUE");
        this.maxValueLabelText = this.getLocalizationString("Max_Value_Title", "MAXIMUM VALUE");
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.min || changes.max || changes.thresholdData) {
            if (changes.min) {
                this.min = changes.min.currentValue;
                this.prevMinValue = this.min;
            }
            if (changes.max) {
                this.max = changes.max.currentValue;
                this.prevMaxValue = this.max;
            }
        }
        if (changes.thresholdData && changes.thresholdData.currentValue) {
            const hasEnabled = this.thresholdData.some(item => item.enabled);
            // --- Calculate startPoints BEFORE modifying thresholdData[0].right ---
            if (hasEnabled && this.thresholdData[this.thresholdData.length - 1]?.left === 0 && this.thresholdData[0]?.right === 0) {
                this.isExistingData = true;
                this.updateStartPointsAndProcessThresholds();
                this.updateFirstThresholdRight();
            } else {
                this.updateStartPointsAndProcessThresholds();
            }
        }
        if (changes.isQualityEvaluationWidget?.currentValue) {
            this.isQualityEvaluationWidget = changes.isQualityEvaluationWidget.currentValue;
        }
        this.setMinMaxValuesFromThresholdData();
        this.patchDataToSlider();
        if (changes.resetThresholdErrors?.currentValue) {
            this.resetThresholdsAndErrors();
        }
        if (changes.metricConversionType) {
            this.minError = '';
            this.maxError = '';
        }
    }

    /**
     * Calculate 25% of thresholdData.right and add to thresholdData[0].right
     */
    public updateFirstThresholdRight(): void {
        const enabledThresholds = this.thresholdData.filter(item => item.enabled);
        const percentageMax = 100;
        if (enabledThresholds.length > 0) {
            const left = Number(this.thresholdData[0].left);
            if (this.metricConversionType === MetricConversionType.Percentage && this.thresholdData[0].enabled && left < percentageMax) {
                this.thresholdData[0].right = percentageMax;
            } else if (this.thresholdData[0].enabled && !isNaN(left) && left !== 0) {
                this.thresholdData[0].right = left + (left * this.percentValueToAdd);
            } else {
                const firstEnabledRight = Number(enabledThresholds[0].right);
                const thousandPercent = firstEnabledRight + (firstEnabledRight * this.percentValueToAdd);
                this.thresholdData[0].right = Math.floor(thousandPercent);
            }
        }
    }

    /**
     * Common function to handle repetitive threshold data processing tasks
     * - Updates start points from threshold data
     * - Sets min/max values from threshold data
     * - Patches data to slider
     */
    private updateStartPointsAndProcessThresholds(): void {
        let startPoints: number[] = [];
        let existingDataProcessed = this.thresholdData.length === 3 && this.thresholdData[2].left === 0 && this.isFirstThresholdRightValid();
        if (existingDataProcessed || (this.isExistingData && this.thresholdData && this.thresholdData.length > 0)) {
            startPoints = this.calculateStartPointsFromExistingData();
            this.isExistingData = false;
        } else if (this.thresholdData && this.thresholdData.length > 1) {
            startPoints = this.thresholdData
                .slice(1)
                .filter(item => item.enabled)
                .map(item => this.isTimeMetric ? this.parseValue(item.right.toString()) : Number(item.right))
                .sort((a, b) => a - b);
        }
        if (!isEqual(this.startPoints, startPoints)) {
            this.startPoints = startPoints;
        }
    }

    public isFirstThresholdRightValid(): boolean {
        const absValue = 0.0001;
        let sourceValue: number | null = null;
        if (this.thresholdData[0].enabled) {
            sourceValue = Number(this.thresholdData[0].left);
        } else if (this.thresholdData[1]?.enabled) {
            sourceValue = Number(this.thresholdData[1].right);
        } else if (this.thresholdData[2]?.enabled) {
            sourceValue = Number(this.thresholdData[2].right);
        }
        if (sourceValue === null) {
            return false;
        }
        const expectedRight = sourceValue + (sourceValue * this.percentValueToAdd);
        const actualRight = Number(this.thresholdData[0].right);
        return Math.abs(actualRight - expectedRight) < absValue;
    }

    private calculateStartPointsFromExistingData(): number[] {
        let startPoints: number[] = [];
        // Find the index of the first enabled threshold
        const firstEnabledIdx = this.thresholdData.findIndex(item => item.enabled);
        if (firstEnabledIdx !== -1) {
            // Include left of the first enabled threshold only if not zero
            const firstEnabled = this.thresholdData[firstEnabledIdx];
            const firstLeft = typeof firstEnabled.left === 'string'
                ? this.parseValue(firstEnabled.left.toString())
                : Number(firstEnabled.left);
            if (firstEnabledIdx === 0 && firstLeft !== 0) {
                startPoints.push(firstLeft);
            }
            // For all enabled thresholds, include right if not zero
            this.thresholdData.forEach((item, index) => {
                if (item.enabled) {
                    const right = typeof item.right === 'string'
                        ? this.parseValue(item.right.toString())
                        : Number(item.right);
                    if (right !== 0 && index !== 0) {
                        startPoints.push(right);
                    }
                }
            });
            startPoints = Array.from(new Set(startPoints)).sort((a, b) => a - b);
        }
        return startPoints;
    }

    public setMinMaxValuesFromThresholdData() {
        if (this.startPoints?.length) {
            // For timer metrics, the threshold data values are already in seconds (numeric)
            // but we need to ensure proper conversion for display
            let maxValue = this.getMaxValueFromThresholdData();
            let minValue = this.thresholdData[this.thresholdData.length - 1]?.left as number;

            // If threshold data contains string values (HH:MM:SS), parse them to seconds
            if (typeof this.thresholdData[0]?.right === 'string') {
                maxValue = this.parseValue(this.thresholdData[0]?.right);
            }
            if (typeof this.thresholdData[this.thresholdData.length - 1]?.left === 'string') {
                minValue = this.parseValue(this.thresholdData[this.thresholdData.length - 1]?.left.toString());
            }

            this.max = maxValue;
            this.min = minValue;
        }
    }

    public getMaxValueFromThresholdData(): number {
        return typeof this.thresholdData[0]?.right === 'string'
            ? this.parseValue(this.thresholdData[0]?.right)
            : Number(this.thresholdData[0]?.right);
    }

    public resetThresholdsAndErrors(): void {
        this.isConflictOverrideError = false;
        if (this.minValueInput) {
            this.minValueInput.writeValue(this.min.toString());
            this.minError = '';
            this.prevMinValue = this.min;
        }
        if (this.maxValueInput) {
            this.maxValueInput.writeValue(this.max.toString());
            this.maxError = '';
            this.prevMaxValue = this.max;
        }
        this.rangesError.emit({ isMinError: !!this.minError, isMaxError: !!this.maxError, isConflictOverrideError: this.isConflictOverrideError });
    }

    private patchDataToSlider() {
        if (this.thresholdData && this.thresholdData.length > 0) {
            // Convert threshold data to ranges format expected by CxoneRangeSlider
            const ranges = this.thresholdData.map(threshold => ({
                enabled: threshold.enabled,
                left: typeof threshold.left === 'string' ? this.parseValue(threshold.left) : threshold.left,
                right: typeof threshold.right === 'string' ? this.parseValue(threshold.right) : threshold.right
            }));
            // Emit the patched data
            this.rangesCreated.emit(ranges);
        }
    }

    private parseValue(value: string): number {
        if (value.includes(':')) {
            // Parse time format (HH:MM:SS)
            const parts = value.split(':');
            if (parts.length === 3) {
                const hours = parseInt(parts[0], 10) || 0;
                const minutes = parseInt(parts[1], 10) || 0;
                const seconds = parseInt(parts[2], 10) || 0;
                return hours * 3600 + minutes * 60 + seconds;
            }
        }
        return parseFloat(value) || 0;
    }

    private validateTimeFormat(value: string): { isValid: boolean, error: string } {
        const timeRegex = /^(\d+):(\d{2}):(\d{2})$/;
        const match = timeRegex.exec(value);

        if (!match) {
            return { isValid: false, error: this.getLocalizationString("Invalid_Time_Format", "Enter time in HH:MM:SS format") };
        }

        const hours = parseInt(match[1], 10);
        const minutes = parseInt(match[2], 10);
        const seconds = parseInt(match[3], 10);

        if (hours < 0 || minutes < 0 || minutes >= 60 || seconds < 0 || seconds >= 60) {
            return { isValid: false, error: this.getLocalizationString("Invalid_Time_Format", "Enter time in HH:MM:SS format") };
        }

        return { isValid: true, error: '' };
    }

    public formatTimeValue(seconds: number): string {
        const totalSeconds = Math.max(0, Math.floor(seconds));
        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const secs = totalSeconds % 60;
        return `${hours.toString()}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }

    private updateSliderBounds() {
        if (this.cxoneRangeSlider) {
            this.cxoneRangeSlider.min = this.min;
            this.cxoneRangeSlider.max = this.max;
        }
    }

    public parsedValuesBasedOnMetricType(value: number): number {
        switch (this.metricConversionType) {
            case MetricConversionType.Percentage:
            case MetricConversionType.Number:
            case MetricConversionType.Seconds:
                return value;
            case MetricConversionType.Minutes:
                return value * 60;
            case MetricConversionType.Hours:
                return value * 3600;
            default:
                return value;
        }
    }

    // Helper method to get the display value for inputs
    public getDisplayValue(value: number): string {
        if (value === null || value === undefined) {
            return '';
        }
        switch (this.metricConversionType) {
            case MetricConversionType.Percentage:
            case MetricConversionType.Number:
            case MetricConversionType.Seconds:
                return value.toString();
            case MetricConversionType.Minutes:
                return Math.floor(value / 60).toString();
            case MetricConversionType.Hours:
                return Math.floor(value / 3600).toString();
            case MetricConversionType.Time:
                return this.formatTimeValue(value);
            default:
                return value.toString();
        }
    }

    private getComparisonValue(commonMetricTypes: MetricConversionType[], isMin: boolean, numValue: number): number {
        let compareValue: number;
        if (commonMetricTypes.includes(this.metricConversionType)) {
            if (isMin) {
                this.prevMinValue = numValue;
                this.minValueUpdated = true;
                if (!this.minMaxError && !this.maxValueUpdated) {
                    this.prevMaxValue = !!this.maxError ? this.prevMaxValue : this.max;
                }
            } else {
                this.prevMaxValue = numValue;
                this.maxValueUpdated = true;
                if (!this.minMaxError && !this.minValueUpdated) {
                    this.prevMinValue = !!this.minError ? this.prevMinValue : this.min;
                }
            }
            this.minMaxError = !!this.minError && !!this.maxError;
            compareValue = isMin ? this.prevMaxValue : this.prevMinValue;
        } else {
            compareValue = isMin ? this.max : this.min;
        }
        return compareValue;
    }

    public validateAndSetValue(value: string, isMin: boolean): void {
        const commonMetricTypes = [MetricConversionType.Number, MetricConversionType.Percentage, MetricConversionType.Seconds, MetricConversionType.Minutes, MetricConversionType.Hours];
        let numValue = this.setNumValueWithMetricType(value, commonMetricTypes);
        const fieldName = isMin ? 'min' : 'max';
        const errorProperty = isMin ? 'minError' : 'maxError';
        let compareValue = this.getComparisonValue(commonMetricTypes, isMin, numValue);

        // Handle time format validation for metric type 4 only
        if (this.isTimeMetric) {
            const timeValidation = this.validateTimeFormat(value);
            if (!timeValidation.isValid) {
                this[errorProperty] = timeValidation.error;
                this.rangesError.emit({ isMinError: !!this.minError, isMaxError: !!this.maxError, isConflictOverrideError: this.isConflictOverrideError });
                return;
            }
            numValue = this.parseValue(value);
        } else {
            if (!value || isNaN(numValue)) {
                this[errorProperty] = this.getLocalizationString("Invalid_Number", "Enter a numerical value");
                this.rangesError.emit({ isMinError: !!this.minError, isMaxError: !!this.maxError, isConflictOverrideError: this.isConflictOverrideError });
                return;
            }
        }

        let validationResultMin = null;
        let validationResultMax = null;
        validationResultMin = this.validateValueRange(numValue, compareValue, isMin);
        if (compareValue !== null) {
            validationResultMax = this.validateValueRange(compareValue, numValue, !isMin);
        }
        this[errorProperty] = validationResultMin.error ? this.getLocalizationString(validationResultMin.error, RANGE_SLIDER_ERRORS[validationResultMin.error]) : "";
        if (!Number.isNaN(compareValue) && validationResultMin?.error === '' && validationResultMax?.error === '') {
            if (commonMetricTypes.includes(this.metricConversionType)) {
                this.maxError = '';
                this.minError = '';
                this.min = this.prevMinValue;
                this.max = this.prevMaxValue;
                this.minValueUpdated = false;
                this.maxValueUpdated = false;
            }
            this[fieldName] = validationResultMin.value;
            this.rangeChange.emit({ min: this.min, max: this.max });
            this.rangesError.emit({ isMinError: !!this.minError, isMaxError: !!this.maxError, isConflictOverrideError: this.isConflictOverrideError });
        } else {
            this.rangesError.emit({ isMinError: !!this.minError, isMaxError: !!this.maxError, isConflictOverrideError: this.isConflictOverrideError });
            return;
        }
    }

    private setNumValueWithMetricType(value: string, commonMetricTypes: MetricConversionType[]): number {
        if (value === '' && commonMetricTypes.includes(this.metricConversionType)) {
            return null;
        } else {
            return this.parsedValuesBasedOnMetricType(Number(value));
        }
    }

    public validateValueRange(value: number, compareValue: number, isMin: boolean): { value: number, error: string } {
        let error = '';
        const commonMetricTypeError = [MetricConversionType.Seconds, MetricConversionType.Minutes, MetricConversionType.Hours].includes(this.metricConversionType);
        if (this.metricConversionType === MetricConversionType.Percentage) {
            error = this.getPercentageValidationError(value, isMin);
        } else if (this.metricConversionType === MetricConversionType.Number) {
            error = this.getErrorForNumberMetricConversionType(value, isMin);
        } else if (this.isTimeMetric) {
            // For time metric type 4 only, validate against basic bounds
            if (value < 0) {
                error = "Min_Less_Than_Zero";
            }
        } else if (commonMetricTypeError) {
            if (isMin && value < 0) {
                error = "Min_Less_Than_Zero";
            } else if (!isMin && value < 0) {
                error = "Max_Less_Than_Zero";
            }
        }

        error = this.getCompareValueValidationError(value, compareValue, isMin, error);
        return { value: value, error };
    }

    private getErrorForNumberMetricConversionType(value: number, isMin: boolean): string {
        let error = '';
        if (isMin && value < -1000) {
            error = "Min_Less_Than_Neg_1000";
        } else if (!isMin && value < -1000) {
            error = "Max_Less_Than_Neg_1000";
        }
        return error;
    }

    private getPercentageValidationError(value: number, isMin: boolean): string {
        let error = '';
        if (isMin && value < 0) {
            error = "Min_Less_Than_Zero";
        } else if (isMin && value > 100) {
            error = "Min_Greater_Than_100";
        } else if (!isMin && value < 0) {
            error = "Max_Less_Than_Zero";
        } else if (!isMin && value > 100) {
            error = "Max_Greater_Than_100";
        }
        return error;
    }

    private getCompareValueValidationError(value: number, compareValue: number, isMin: boolean, currentError: string): string {
        let error = currentError;
        if (!error && value === compareValue) {
            error = "Min_Max_Same";
        }
        if (!error) {
            if (isMin && value >= compareValue) {
                error = "Min_Not_Less_Than_Max";
            } else if (!isMin && value <= compareValue) {
                error = "Max_Not_Greater_Than_Min";
            }
        }
        this.setMinMaxValuesForTimeMetricConversionType(isMin, value, error);
        return error;
    }

    private setMinMaxValuesForTimeMetricConversionType(isMin: boolean, value: number, error: string): void {
        if (this.metricConversionType === MetricConversionType.Time) {
            if (!error) {
                this.min = isMin ? value : this.min;
                this.max = !isMin ? value : this.max;
            }
        }
    }

    removeRangeFromSlider() {
        this.cxoneRangeSlider.removeRangeFromSlider();
    }
    addRangeToSlider() {
        this.cxoneRangeSlider.addRangeToSlider();
    }

    onRangeChange(event) {
        if (event && Array.isArray(event)) {
            const thresholdFormat = event.map(range => ({
                enabled: range.enabled ?? true,
                left: range.left,
                right: range.right
            }));
            this.rangesCreated.emit(thresholdFormat);
        } else {
            this.rangesCreated.emit(event);
        }
        this.isConflictOverrideError = false;
    }

    onRangesOverlap(event: boolean) {
        this.isConflictOverrideError = event ?? false;
        this.rangesError.emit({ isConflictOverrideError: this.isConflictOverrideError, isMinError: !!this.minError, isMaxError: !!this.maxError });
    }

    getLocalizationString(key: string, defaultValue: string): string {
        let returnString = this.localizationService.getCachedText("CXOne_Range_Slider", key, defaultValue);
        return returnString;
    }
}